import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Transporter } from '../models/transporter.model';
import { Transport } from '../models/transport.model';
import { ICustomer } from '../models/customer.model';
import { User } from '../models/user.model';
import { take } from 'rxjs/operators';
import { ThemeView } from '../models/theme-view.model';
import { NotificationViewDto } from '../models/notification-view-dto';
import { Pagination } from '../models/pagination.model';
import { CurrentUserBasket } from '../models/current-user-basket.model';

@Injectable({
  providedIn: 'root',
})
export class CurrentDataService {
  private currentTransporter?: BehaviorSubject<Transporter>;
  private currentTransport?: BehaviorSubject<Transport | undefined>;
  private transportInModification?: BehaviorSubject<Transport | undefined>;
  private user?: BehaviorSubject<User | undefined>;
  private account?: BehaviorSubject<User | undefined>;
  private customer?: BehaviorSubject<ICustomer | undefined>;
  private basket?: BehaviorSubject<CurrentUserBasket | undefined>;
  private currentTheme?: BehaviorSubject<ThemeView | undefined>;
  private currentNotifications?: BehaviorSubject<{ content: NotificationViewDto[]; pagination: Pagination }>;

  public getCurrentTransporter(): Observable<Transporter> {
    if (!this.currentTransporter) {
      this.currentTransporter = new BehaviorSubject<Transporter>(new Transporter());
    }
    return this.currentTransporter.asObservable().pipe(take(1));
  }

  public updateTransporter(transporter: Transporter): void {
    if (!this.currentTransporter) {
      this.currentTransporter = new BehaviorSubject<Transporter>(transporter);
    }
    this.currentTransporter.next(transporter);
  }

  public getCurrentTransport(): Observable<Transport | undefined> {
    if (!this.currentTransport) {
      this.currentTransport = new BehaviorSubject<Transport | undefined>(undefined);
    }
    return this.currentTransport.asObservable();
  }

  public updateCurrentTransport(transport: Transport): void {
    if (!this.currentTransport) {
      this.currentTransport = new BehaviorSubject<Transport | undefined>(transport);
    }
    this.currentTransport.next(transport);
  }

  public removeCurrentTransport(): void {
    this.currentTransport?.next(undefined);
  }

  public getTransportInModification(): Observable<Transport | undefined> {
    if (!this.transportInModification) {
      this.transportInModification = new BehaviorSubject<Transport | undefined>(undefined);
    }
    return this.transportInModification.asObservable();
  }

  public updateTransportInModification(transport: Transport | undefined): void {
    if (!this.transportInModification) {
      this.transportInModification = new BehaviorSubject<Transport | undefined>(transport);
    }
    this.transportInModification.next(transport);
  }

  public removeTransportInModification(): void {
    this.transportInModification?.next(undefined);
  }

  public getCustomer(): Observable<ICustomer | undefined> {
    if (!this.customer) {
      this.customer = new BehaviorSubject<ICustomer | undefined>(undefined);
    }
    return this.customer.asObservable().pipe(take(1));
  }

  public updateCustomer(customer: ICustomer | undefined): void {
    if (!this.customer) {
      this.customer = new BehaviorSubject<ICustomer | undefined>(customer);
    }
    this.customer.next(customer);
  }

  public getUser(): Observable<User | undefined> {
    if (!this.user) {
      this.user = new BehaviorSubject<User | undefined>(undefined);
    }
    return this.user.asObservable();
  }

  public updateUser(account?: User): void {
    if (!this.user) {
      this.user = new BehaviorSubject<User | undefined>(account);
    }
    this.user.next(account);
  }

  public getAccount(): Observable<User | undefined> {
    if (!this.account) {
      this.account = new BehaviorSubject<User | undefined>(undefined);
    }
    return this.account.asObservable();
  }

  public updateAccount(account: User | undefined): void {
    if (!this.account) {
      this.account = new BehaviorSubject<User | undefined>(account);
    }
    this.account.next(account);
  }

  public getCurrentBasket(): Observable<CurrentUserBasket | undefined> {
    if (!this.basket) {
      this.basket = new BehaviorSubject<CurrentUserBasket | undefined>(undefined);
    }
    return this.basket.asObservable();
  }

  public updateBasket(basket?: CurrentUserBasket): void {
    if (!this.basket) {
      this.basket = new BehaviorSubject<CurrentUserBasket | undefined>(basket);
    }
    this.basket.next(basket);
  }

  public handleLogout(): void {
    this.account?.next(undefined);
    this.user?.next(undefined);
    this.customer?.next(undefined);
    this.currentTransport?.next(undefined);
    this.transportInModification?.next(undefined);
  }

  public getCurrentTheme(): Observable<ThemeView | undefined> {
    if (!this.currentTheme) {
      this.currentTheme = new BehaviorSubject<ThemeView | undefined>(undefined);
    }
    return this.currentTheme.asObservable();
  }

  public updateCurrentTheme(theme: ThemeView): void {
    if (!this.currentTheme) {
      this.currentTheme = new BehaviorSubject<ThemeView | undefined>(theme);
    }
    this.currentTheme.next(theme);
  }

  public getCurrentNotifications(): Observable<{ content: NotificationViewDto[]; pagination: Pagination }> {
    if (!this.currentNotifications) {
      this.currentNotifications = new BehaviorSubject<{
        content: NotificationViewDto[];
        pagination: Pagination;
      }>({ content: [], pagination: new Pagination() });
    }
    return this.currentNotifications.asObservable();
  }

  public updateCurrentNotifications(notifications: { content: NotificationViewDto[]; pagination: Pagination }): void {
    if (!this.currentNotifications) {
      this.currentNotifications = new BehaviorSubject<{
        content: NotificationViewDto[];
        pagination: Pagination;
      }>(notifications);
    }
    return this.currentNotifications.next(notifications);
  }
}
